import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { FixedAssetValue } from '@/hooks/useFixedAssetValues';

interface ValueHistoryChartProps {
  values: FixedAssetValue[];
  currency?: string;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function CustomTooltip({ active, payload, currency }: any) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm text-muted-foreground mb-1">{formatDate(point.recorded_at)}</p>
      <p className="text-base font-semibold">{formatCurrency(point.value, currency)}</p>
      {point.notes && <p className="text-xs text-muted-foreground mt-1">{point.notes}</p>}
    </div>
  );
}

export function ValueHistoryChart({ values, currency = 'USD' }: ValueHistoryChartProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Value History</CardTitle>
      </CardHeader>
      <CardContent>
        {values.length < 2 ? (
          <div
            className="h-[200px] flex items-center justify-center"
            data-testid="insufficient-data-message"
          >
            <p className="text-muted-foreground text-sm">
              Add more data points to see a trend
            </p>
          </div>
        ) : (
          <div
            role="img"
            aria-label={`Value history chart showing ${values.length} data points`}
            data-testid="value-history-chart"
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={values}
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="recorded_at"
                  tickFormatter={formatDate}
                  className="text-xs"
                />
                <YAxis
                  tickFormatter={(v) => formatCurrency(v, currency)}
                  className="text-xs"
                />
                <Tooltip content={<CustomTooltip currency={currency} />} />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
